import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import MainContent from './components/MainContent';
import Footer from './components/Footer';
import About from './components/About';
// Import static data as fallback
import staticNewsData from './data.json';

// Define the data structure
interface NewsData {
  categories: Array<{
    name: string;
    links: Array<{
      title: string;
      url: string;
      highlight: boolean;
      imageSrc?: string;
      fullImageSrc?: string;
      comment?: string;
    }>;
  }>;
}

/**
 * Main App component that renders the entire application
 * Now includes routing between Home and About pages
 */
const App: React.FC = () => {
  const [newsData, setNewsData] = useState<NewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use port 3001 for the API
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/api/news`);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Validate the data structure
        if (!data.categories || !Array.isArray(data.categories)) {
          console.error('Invalid data structure received from API:', data);
          throw new Error('Invalid data structure received from API');
        }
        
        setNewsData(data);
      } catch (err) {
        console.error('Failed to fetch from API, using static data:', err);
        
        // Make sure static data has the correct structure
        if (!staticNewsData.categories || !Array.isArray(staticNewsData.categories)) {
          console.error('Invalid static data structure:', staticNewsData);
          setError('Error: Invalid data structure in both API and static data');
        } else {
          // Fall back to static data
          setNewsData(staticNewsData as NewsData);
          setError('Using local data (API connection failed)');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div className="loading">Loading...</div>;

  // Add additional error handling
  if (!newsData || !newsData.categories) {
    return (
      <div className="error-container">
        <h1>Error Loading Data</h1>
        <p>Could not load news data. Please try again later.</p>
        {error && <p className="error-message">{error}</p>}
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <div className="app">
            <Header 
              siteName="Alternatívny žurnalista" 
              tagline="Simple. Old skool. Fast. Text-based." 
            />
            {error && <div className="api-error">{error}</div>}
            <MainContent categories={newsData.categories} />
            <Footer />
          </div>
        } />
        <Route path="/about" element={<About />} />
      </Routes>
    </Router>
  );
};

export default App; 